import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudLightning, CloudSnow } from "@phosphor-icons/react";

// Fort Lauderdale / Wilton Manors area
const LAT = 26.1426;
const LON = -80.1426;
const URL =
  `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
  `&current=temperature_2m,weather_code` +
  `&daily=weather_code,temperature_2m_max,temperature_2m_min` +
  `&temperature_unit=fahrenheit&timezone=America%2FNew_York&forecast_days=5`;

function iconFor(code) {
  if (code === 0) return Sun;
  if (code === 1 || code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 95) return CloudLightning;
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return CloudRain;
  return CloudSun;
}

const dayName = (iso, i) =>
  i === 0 ? "Today" : new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });

export default function WeatherWidget({ className = "" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(URL)
      .then((r) => r.json())
      .then((j) => {
        if (!alive || !j || !j.current) return;
        const daily = (j.daily?.time || []).map((t, i) => ({
          date: t,
          code: j.daily.weather_code[i],
          max: Math.round(j.daily.temperature_2m_max[i]),
          min: Math.round(j.daily.temperature_2m_min[i]),
        }));
        setData({ temp: Math.round(j.current.temperature_2m), code: j.current.weather_code, daily });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (!data) return null;
  const Icon = iconFor(data.code);

  return (
    <div
      ref={wrapRef}
      className={`relative ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid="weather-widget"
        onClick={() => setOpen((o) => !o)}
        title="Fort Lauderdale weather — 5-day forecast"
        className="flex items-center gap-2 text-emerald hover:text-sunset transition-colors duration-300"
      >
        <Icon size={20} weight="duotone" className="text-sunset" />
        <span className="text-sm font-medium tabular-nums" data-testid="weather-temp">{data.temp}°F</span>
        <span className="hidden lg:inline text-xs text-emerald-soft">Fort Lauderdale</span>
      </button>

      <AnimatePresence>
        {open && data.daily.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="weather-forecast"
            className="absolute left-0 top-full mt-3 w-[300px] rounded-2xl bg-sand/95 backdrop-blur-xl border border-emerald/10 shadow-[0_20px_60px_-25px_rgba(15,26,21,0.4)] p-4 z-50"
          >
            <p className="overline text-sunset mb-3 px-1">5-day forecast · Fort Lauderdale</p>
            <div className="flex flex-col">
              {data.daily.map((d, i) => {
                const DIcon = iconFor(d.code);
                return (
                  <div
                    key={d.date}
                    data-testid={`forecast-day-${i}`}
                    className="flex items-center justify-between py-2 px-1 border-b border-emerald/8 last:border-0"
                  >
                    <span className="text-sm text-emerald w-14">{dayName(d.date, i)}</span>
                    <DIcon size={20} weight="duotone" className="text-sunset" />
                    <span className="text-sm tabular-nums text-emerald-soft">
                      <span className="text-emerald font-medium">{d.max}°</span> / {d.min}°
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
